import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  Clock,
  Star,
  Send,
  RefreshCw,
  MessageSquare,
  Loader2,
  Globe,
  AlertTriangle,
  CalendarDays,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { aiAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';

const LANGUAGES = [
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

const I18N = {
  nl: {
    title: 'AI Inzichten', subtitle: 'Bevindingen uit je eigen bestellingen van de laatste 90 dagen',
    refresh: 'Vernieuwen', languageLabel: 'AI-taal',
    findings: 'Bevindingen', basedOn: 'Op basis van {n} bestellingen',
    figures: 'Je cijfers', busiestHours: 'Drukste uren', busiestDays: 'Drukste dagen',
    topSellers: 'Bestverkocht', orders: 'bestellingen', sold: 'verkocht',
    revenue: 'Omzet', avgOrder: 'Gemiddelde bon', customers: 'Klanten', returning: 'kwamen terug',
    neverSold: 'Niet verkocht in deze periode',
    chatTitle: 'Vraag iets over je cijfers', chatSubtitle: 'Antwoorden komen uitsluitend uit je eigen data',
    chatEmpty: 'Stel een vraag over je bestellingen',
    chatPlaceholder: 'Bijv. welke gerechten lopen slecht?', thinking: 'Bezig…',
    quickTitle: 'Snelle vragen',
    quickQs: [
      'Welke gerechten lopen slecht?',
      'Wanneer is het het drukst?',
      'Hoeveel klanten bestellen opnieuw?',
      'Wat is er opvallend aan mijn omzet?',
    ],
    errTitle: 'De analyse lukte niet',
    errBody: 'Er ging iets mis bij het ophalen van je inzichten. Je gegevens zijn niet aangepast.',
    retry: 'Opnieuw proberen',
  },
  en: {
    title: 'AI Insights', subtitle: 'Findings from your own orders over the last 90 days',
    refresh: 'Refresh', languageLabel: 'AI language',
    findings: 'Findings', basedOn: 'Based on {n} orders',
    figures: 'Your figures', busiestHours: 'Busiest hours', busiestDays: 'Busiest days',
    topSellers: 'Best sellers', orders: 'orders', sold: 'sold',
    revenue: 'Revenue', avgOrder: 'Average order', customers: 'Customers', returning: 'came back',
    neverSold: 'Not sold in this period',
    chatTitle: 'Ask about your figures', chatSubtitle: 'Answers come only from your own data',
    chatEmpty: 'Ask a question about your orders',
    chatPlaceholder: 'E.g. which dishes sell poorly?', thinking: 'Working…',
    quickTitle: 'Quick questions',
    quickQs: [
      'Which dishes sell poorly?',
      'When am I busiest?',
      'How many customers order again?',
      'What stands out about my revenue?',
    ],
    errTitle: 'The analysis failed',
    errBody: 'Something went wrong fetching your insights. Your data was not changed.',
    retry: 'Try again',
  },
  fr: {
    title: 'Analyses IA', subtitle: 'Constats tirés de vos commandes des 90 derniers jours',
    refresh: 'Actualiser', languageLabel: 'Langue IA',
    findings: 'Constats', basedOn: 'Sur la base de {n} commandes',
    figures: 'Vos chiffres', busiestHours: 'Heures de pointe', busiestDays: 'Jours de pointe',
    topSellers: 'Meilleures ventes', orders: 'commandes', sold: 'vendus',
    revenue: "Chiffre d'affaires", avgOrder: 'Panier moyen', customers: 'Clients', returning: 'sont revenus',
    neverSold: 'Non vendus sur la période',
    chatTitle: 'Posez une question sur vos chiffres', chatSubtitle: 'Les réponses proviennent uniquement de vos données',
    chatEmpty: 'Posez une question sur vos commandes',
    chatPlaceholder: 'Ex. quels plats se vendent mal ?', thinking: 'En cours…',
    quickTitle: 'Questions rapides',
    quickQs: [
      'Quels plats se vendent mal ?',
      'Quand suis-je le plus occupé ?',
      'Combien de clients recommandent ?',
      'Que remarque-t-on sur mon chiffre ?',
    ],
    errTitle: "L'analyse a échoué",
    errBody: "Une erreur s'est produite. Vos données n'ont pas été modifiées.",
    retry: 'Réessayer',
  },
  de: {
    title: 'KI-Einblicke', subtitle: 'Erkenntnisse aus deinen Bestellungen der letzten 90 Tage',
    refresh: 'Aktualisieren', languageLabel: 'KI-Sprache',
    findings: 'Erkenntnisse', basedOn: 'Auf Basis von {n} Bestellungen',
    figures: 'Deine Zahlen', busiestHours: 'Stoßzeiten', busiestDays: 'Stärkste Tage',
    topSellers: 'Bestseller', orders: 'Bestellungen', sold: 'verkauft',
    revenue: 'Umsatz', avgOrder: 'Durchschnittsbon', customers: 'Kunden', returning: 'kamen wieder',
    neverSold: 'In diesem Zeitraum nicht verkauft',
    chatTitle: 'Frag nach deinen Zahlen', chatSubtitle: 'Antworten kommen ausschließlich aus deinen Daten',
    chatEmpty: 'Stelle eine Frage zu deinen Bestellungen',
    chatPlaceholder: 'Z. B. welche Gerichte laufen schlecht?', thinking: 'Arbeite…',
    quickTitle: 'Schnelle Fragen',
    quickQs: [
      'Welche Gerichte laufen schlecht?',
      'Wann ist am meisten los?',
      'Wie viele Kunden bestellen erneut?',
      'Was fällt beim Umsatz auf?',
    ],
    errTitle: 'Die Analyse ist fehlgeschlagen',
    errBody: 'Beim Abrufen ist etwas schiefgelaufen. Deine Daten wurden nicht geändert.',
    retry: 'Erneut versuchen',
  },
  es: {
    title: 'Análisis con IA', subtitle: 'Hallazgos de tus pedidos de los últimos 90 días',
    refresh: 'Actualizar', languageLabel: 'Idioma de la IA',
    findings: 'Hallazgos', basedOn: 'Basado en {n} pedidos',
    figures: 'Tus cifras', busiestHours: 'Horas punta', busiestDays: 'Días punta',
    topSellers: 'Más vendidos', orders: 'pedidos', sold: 'vendidos',
    revenue: 'Ingresos', avgOrder: 'Ticket medio', customers: 'Clientes', returning: 'repitieron',
    neverSold: 'No vendidos en este periodo',
    chatTitle: 'Pregunta sobre tus cifras', chatSubtitle: 'Las respuestas salen solo de tus datos',
    chatEmpty: 'Haz una pregunta sobre tus pedidos',
    chatPlaceholder: 'P. ej. ¿qué platos se venden poco?', thinking: 'Trabajando…',
    quickTitle: 'Preguntas rápidas',
    quickQs: [
      '¿Qué platos se venden poco?',
      '¿Cuándo hay más movimiento?',
      '¿Cuántos clientes repiten?',
      '¿Qué destaca en mis ingresos?',
    ],
    errTitle: 'El análisis falló',
    errBody: 'Algo salió mal al obtener los datos. Tu información no se modificó.',
    retry: 'Reintentar',
  },
  it: {
    title: 'Analisi AI', subtitle: 'Osservazioni dai tuoi ordini degli ultimi 90 giorni',
    refresh: 'Aggiorna', languageLabel: 'Lingua AI',
    findings: 'Osservazioni', basedOn: 'Su {n} ordini',
    figures: 'I tuoi numeri', busiestHours: 'Ore di punta', busiestDays: 'Giorni di punta',
    topSellers: 'Più venduti', orders: 'ordini', sold: 'venduti',
    revenue: 'Ricavi', avgOrder: 'Scontrino medio', customers: 'Clienti', returning: 'sono tornati',
    neverSold: 'Non venduti nel periodo',
    chatTitle: 'Chiedi dei tuoi numeri', chatSubtitle: 'Le risposte vengono solo dai tuoi dati',
    chatEmpty: 'Fai una domanda sui tuoi ordini',
    chatPlaceholder: 'Es. quali piatti vendono poco?', thinking: 'In corso…',
    quickTitle: 'Domande rapide',
    quickQs: [
      'Quali piatti vendono poco?',
      "Quand'è il momento più intenso?",
      'Quanti clienti riordinano?',
      'Cosa spicca nei ricavi?',
    ],
    errTitle: "L'analisi non è riuscita",
    errBody: 'Qualcosa è andato storto. I tuoi dati non sono stati modificati.',
    retry: 'Riprova',
  },
};

const AI_LANG_KEY = 'foodflows_ai_language';

export const AIInsightsPage = () => {
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem(AI_LANG_KEY) || 'nl'; } catch { return 'nl'; }
  });
  const t = I18N[language] || I18N.nl;

  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(AI_LANG_KEY, language); } catch { /* ignore */ }
  }, [language]);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await aiAPI.getInsights({ language });
      setInsights(data);
    } catch (e) {
      // Nette melding in de pagina zelf, niet alleen een toast die wegvalt:
      // de gebruiker moet kunnen zien wat er mis is en het opnieuw kunnen proberen.
      setError(e.response?.data?.detail || null);
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    const id = setTimeout(() => {
      fetchInsights();
      setChatMessages([]);
    }, 0);
    return () => clearTimeout(id);
  }, [fetchInsights]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const vraag = async (tekst) => {
    const vraagTekst = (tekst ?? chatInput).trim();
    if (!vraagTekst || sending) return;
    setChatMessages(prev => [...prev, { role: 'user', content: vraagTekst }]);
    setChatInput('');
    setSending(true);
    try {
      const { data } = await aiAPI.getInsights({ message: vraagTekst, language });
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (e) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: e.response?.data?.detail || t.errBody,
        failed: true,
        question: vraagTekst,
      }]);
    } finally {
      setSending(false);
    }
  };

  const f = insights?.figures || {};

  return (
    <div className="space-y-6" data-testid="ai-insights-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#E07A5F]" />
            {t.title}
          </h1>
          <p className="text-gray-500">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500">{t.languageLabel}:</span>
            <select
              className="text-sm font-medium bg-transparent focus:outline-none cursor-pointer"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              data-testid="ai-language-select"
            >
              {LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
          </div>
          <Button variant="outline" onClick={fetchInsights} disabled={loading} data-testid="refresh-insights-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t.refresh}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Bevindingen */}
        <div className="card-dashboard" data-testid="insights-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#D8F3DC] rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#1A4D2E]" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900">{t.findings}</h3>
              {insights?.orders_analysed > 0 && (
                <p className="text-sm text-gray-500">
                  {t.basedOn.replace('{n}', insights.orders_analysed)}
                </p>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          )}

          {!loading && error !== null && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="insights-error">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900">{t.errTitle}</p>
                  <p className="text-amber-800 text-sm mt-1">{error || t.errBody}</p>
                  <Button onClick={fetchInsights} className="btn-primary mt-4" data-testid="insights-retry">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t.retry}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!loading && error === null && insights && (
            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-[#D8F3DC]/50 to-transparent rounded-xl">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{insights.response}</p>
              </div>

              {f.orders > 0 && (
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-lg font-bold text-gray-900">{formatPrice(f.revenue || 0, 'EUR')}</p>
                    <p className="text-xs text-gray-500">{t.revenue}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-lg font-bold text-gray-900">{formatPrice(f.average_order || 0, 'EUR')}</p>
                    <p className="text-xs text-gray-500">{t.avgOrder}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-lg font-bold text-gray-900">{f.unique_customers || 0}</p>
                    <p className="text-xs text-gray-500">
                      {t.customers} · {f.returning_customers || 0} {t.returning}
                    </p>
                  </div>
                </div>
              )}

              {f.busy_hours?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#E07A5F]" />{t.busiestHours}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {f.busy_hours.map(h => (
                      <span key={h.hour} className="px-3 py-1.5 bg-[#FDF0D5] rounded-lg text-sm">
                        {String(h.hour).padStart(2, '0')}:00 — {h.orders} {t.orders}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {f.busy_days?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-[#E07A5F]" />{t.busiestDays}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {f.busy_days.map(d => (
                      <span key={d.day} className="px-3 py-1.5 bg-[#FDF0D5] rounded-lg text-sm capitalize">
                        {d.day} — {d.orders} {t.orders}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {f.top_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#E07A5F]" />{t.topSellers}
                  </h4>
                  <div className="space-y-2">
                    {f.top_items.map((it, i) => (
                      <div key={it.name} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-700">{i + 1}. {it.name}</span>
                        <span className="text-sm font-medium text-gray-900">{it.count}× {t.sold}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {f.never_sold?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    {t.neverSold} ({f.never_sold_total})
                  </h4>
                  <p className="text-sm text-gray-500">{f.never_sold.join(' · ')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Vragen */}
        <div className="card-dashboard flex flex-col" data-testid="insights-chat-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FDF0D5] rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#E07A5F]" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900">{t.chatTitle}</h3>
              <p className="text-sm text-gray-500">{t.chatSubtitle}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[420px] min-h-[220px]">
            {chatMessages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 py-10">
                <MessageSquare className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">{t.chatEmpty}</p>
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-[#1A4D2E] text-white'
                    : m.failed
                      ? 'bg-amber-50 border border-amber-300 text-amber-900'
                      : 'bg-gray-100 text-gray-800'}`}>
                  {m.content}
                  {m.failed && (
                    <button
                      onClick={() => vraag(m.question)}
                      data-testid="chat-retry"
                      className="mt-2 block text-sm font-medium underline hover:no-underline"
                    >
                      {t.retry}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl px-4 py-2.5 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />{t.thinking}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2">{t.quickTitle}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {t.quickQs.map(q => (
                <button
                  key={q}
                  onClick={() => vraag(q)}
                  disabled={sending}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); vraag(); } }}
                placeholder={t.chatPlaceholder}
                disabled={sending}
                data-testid="insights-chat-input"
                className="h-11"
              />
              <Button onClick={() => vraag()} disabled={sending || !chatInput.trim()}
                      className="btn-primary h-11" data-testid="insights-chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
